import { OkrMethodologyFacade } from "./../components/facades/okr-methodology.facade";
import { IQService, ITimeoutService, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalScope, IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import dayjs from "@gtmhub/dayjs";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { IAssignee } from "@gtmhub/assignees/models";
import {
  IGoal,
  IGoalPoliciesMap,
  IWorkflowUi,
  IGoalPolicy,
  IDesignScoreForUpdate,
  AttainmentTypeString,
  IGoalParent,
  IGoalChildReorderPayload,
  IGoalChildReorderResponse,
  ContributorType,
} from "../models";
import { IAllPoliciesMap } from "@gtmhub/policies/models/models";
import { ITask, TaskService, ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs, TaskEvents } from "@gtmhub/tasks";
import { GoalService } from "../goalService";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { groupGoalsBySession } from "@gtmhub/links";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { PolicyService } from "@gtmhub/policies/services/policyService";
import { MetricService, IMetric, DisableMetricSyncModal, CascadingMetricFormModal, canCascadeMetric } from "@gtmhub/okrs/metrics";
import { GoalModalsService } from "../goalModalsService";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IStateInit } from "@gtmhub/core/routing";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions } from "@gtmhub/assignees/redux/assignee-actions";
import { ItemType } from "@gtmhub/okrs/alignment-tree";
import { applyWorkflowConditions, generateWorkflowUi } from "../workflowUtils";
import { AccountType, isGtmhubAccountId } from "@gtmhub/core";
import { OnboardingService } from "@gtmhub/onboarding";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { UserAction, ISessionsStoreState, IPlanningSession, PlanningSessionsActions, PlanningSessionStatus } from "@gtmhub/sessions";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { getCurrentUserId, hasCurrentUserRole } from "@gtmhub/users";
import { IdMap, toKeyMap } from "@gtmhub/util";
import { prepareCheckinData } from "@gtmhub/web-workers/shared";
import { AssigneeService } from "@gtmhub/assignees/services";
import { handleRequestsInBatches } from "@gtmhub/util";
import { CloneOkrsModal, GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { ProgressColoring } from "@gtmhub/configuration";
import { IEmployee } from "@gtmhub/employees/models/models";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState } from "@gtmhub/employees";
import { isGoalOwnedByTeamOfEmployee } from "@gtmhub/employees/util";
import { IDndDroppedParams, IDndInsertedParams, IOnDndDroppedCbResponse } from "../../shared/components/dnd-list/dnd-list.component";
import { IParentOkrSelectorEntry } from "@gtmhub/shared/components/parent-okr-selector/models";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { getParentState, isOkrStatusAbandoned } from "../utils";
import { copyToClipboard } from "@gtmhub/shared/dom";
import { getCopyURLNotificationEl } from "@gtmhub/shared/utils/dom-utils";
import { localize } from "@gtmhub/localization";
import { IGoalMethodologySettingsUI } from "../components/facades/okr-methodology.facade";

interface IGoalStateParams {
  id: string;
  openingState?: "edit";
  sharedGoal?: string;
}

interface IGoalRedux {
  allSessions: IPlanningSession[];
  assigneesIdMap: IdMap<IAssignee>;
  employee: IEmployee;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState & ICurrentEmployeeStoreState): IGoalRedux => {
  return {
    allSessions: state.sessions.items,
    assigneesIdMap: state.assignees.map,
    employee: state.currentEmployee.info,
    reduxItemsFetched: state.sessions.isFetched && state.assignees.isFetched && state.currentEmployee.isFetched,
    reduxItemsError: state.sessions.error || state.assignees.error || state.currentEmployee.error,
  };
};

export interface IGoalRootScopeService extends IGtmhubRootScopeService {
  planningSession: IPlanningSession;
  ui: {
    maskGrid: boolean;
  };
}

type GoalIndicators = Record<"delete" | "deleting" | "getGoal" | "updatingField" | "loadingGoalProgress" | "loadingSession", boolean>;

type DisallowedMetricCreationReason = "abandoned" | "session-in-progress" | "approved";

export interface IGoalScope extends IModalScope, IExtendedRootScopeService, IGoalRedux {
  isReorderGoalChildrenFeatureEnabled: boolean;
  allowedActions: UserAction[];
  error: string;
  fake: {
    reviewers: string[];
  };
  goal: IGoal;
  parentItem: IGoal | IMetric;
  goalPoliciesMap: IGoalPoliciesMap;
  allPoliciesMap: IAllPoliciesMap;
  indicators: GoalIndicators;
  isTrialAccount: boolean;
  isAllowedToSubmitToMarketplace: boolean;
  parentType: string;
  planningSession: IPlanningSession;
  subGoals: IGoal[];
  goalChildren: ContributorType[];
  taskList: ITask[];
  allowEditGoalFields: boolean;
  isOwnedByEmployee: boolean;
  ui: {
    disabled: boolean;
  };
  workflowUi: IWorkflowUi;
  focusOnNameField: boolean;
  shouldSeeOnboardingExperiment: boolean;
  canChangeClosedStatus: boolean;
  employee: IEmployee;
  okrForParentSelector: Partial<IParentOkrSelectorEntry>;
  sessionsForParentSelector: string[];
  methodologySettings: IGoalMethodologySettingsUI & { isProgressAggregationDisabled: boolean };
  parentState: string;
  closeGoalDetailsModal(): void;
  updateGoalParent(parent: IGoalParent): void;
  clone(): void;
  openCloneModal(): void;
  openMoveModal(): void;
  confirmDeleteGoal(): void;
  confirmDeleteMetric(index: number, metricId: string): void;
  editReviewers(): void;
  setAutofocus(): void;
  updateField(goal: IGoal, fields: string[]): void;
  switchProgressCalculationCriteria(newCriteria: string): void;
  onTextareaClick(event): void;
  cloneMetricHere(metric: IMetric): void;
  openCloneMetricModal(metric: IMetric): void;
  openMoveMetricModal(metric: IMetric): void;
  toggleReviewers(): void;
  selectAssignee(assignee: IAssignee): void;
  selectMultipleOwners(ids: string[]): void;
  changeSession(sessionId: string): void;
  addChildObjective(itemId: string, itemType: ItemType, itemSessionId: string): void;
  openDisableSyncModal(metricId: string): void;
  canUpdateMetric(metric: IMetric): boolean;
  isGoalAbandoned(): boolean;
  metricsContributeToGoalProgress(): boolean;
  goalUnlockedAndSessionOpen(): boolean;
  sessionWorkflowHasMetricFields(): boolean;
  goalUnlockedUnabandonedSessionOpen(): boolean;
  reorderGoalChild(params: IDndDroppedParams<ContributorType>): void;
  onReorderSuccess(params: IDndDroppedParams<ContributorType>): void;
  handleOpenCascadingForm(metric: IMetric, goalName: string): void;
  canCascadeMetric(metric: IMetric): boolean;
  getInfoTooltipReason(): string;
  copyItemUrlToClipboard(itemId: string, type: "metric" | "goal"): void;
  isFieldPartOfWorkflow(fieldName: string): boolean;
}

export class GoalCtrl implements IStateInit {
  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;
  private designScoreForUpdate: IDesignScoreForUpdate;
  private goalDataLoading: boolean;
  private metricId: string;
  private modal: IModalServiceInstance;
  // to do: remove with https://gtmhub.atlassian.net/browse/GVS-9128 along with FeatureToggleV2Service and related code if unused
  private isCloseOkrFeatureEnabled: boolean;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "GoalService",
    "MetricService",
    "AccountResolverService",
    "PlanningSessionsActions",
    "$uibModal",
    "AssigneeActions",
    "PermissionsService",
    "$q",
    "$timeout",
    "UIErrorHandlingService",
    "TaskService",
    "PolicyService",
    "GoalModalsService",
    "$ngRedux",
    "ProgressColoring",
    "OnboardingService",
    "AssigneeService",
    "CloneOkrsModal",
    "DisableMetricSyncModal",
    "CurrentEmployeeActions",
    "FeatureToggleV2Service",
    "CascadingMetricFormModal",
    "OkrMethodologyFacade",
  ];

  constructor(
    private $rootScope: IGoalRootScopeService & ITraceRootScopeService,
    private $scope: IGoalScope,
    private $state: StateService,
    private $stateParams: IGoalStateParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private accountResolverService: AccountResolverService,
    private planningSessionsActions: PlanningSessionsActions,
    private $uibModal: IModalService,
    private assigneeActions: AssigneeActions,
    private permissionsService: PermissionsService,
    private $q: IQService,
    private $timeout: ITimeoutService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private tasksService: TaskService,
    private policyService: PolicyService,
    private goalModalsService: GoalModalsService,
    private $ngRedux: INgRedux,
    private progressColoring: ProgressColoring,
    private onboardingService: OnboardingService,
    private assigneeService: AssigneeService,
    private cloneOkrsModal: CloneOkrsModal,
    private disableMetricSyncModal: DisableMetricSyncModal,
    private currentEmployeeActions: CurrentEmployeeActions,
    private featureToggleService: FeatureToggleV2Service,
    private cascadingMetricFormModal: CascadingMetricFormModal,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    this.taskEvents = new TaskEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.addReturnStateForSharedUrl();
    this.$scope.parentState = getParentState(this.$state.current.name);

    this.$scope.focusOnNameField = this.$stateParams.openingState === "edit";

    this.$rootScope.planningSession = {};
    this.$scope.indicators = {
      delete: false,
      deleting: false,
      getGoal: true,
      updatingField: false,
      loadingGoalProgress: false,
      loadingSession: false,
    };
    this.$rootScope.ui = {
      maskGrid: false,
    };
    this.$scope.ui = {
      disabled: true,
    };
    this.$scope.isTrialAccount = this.accountResolverService.getAccountData().type === AccountType.TrialAccount;
    this.$scope.parentType = "goal";
    this.$scope.allPoliciesMap = {};
    this.$scope.goalPoliciesMap = {};

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    this.$scope.confirmDeleteGoal = this.confirmDeleteGoal;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.setAutofocus = this.setAutofocus;
    this.$scope.editReviewers = this.editReviewers;
    this.$scope.updateField = this.updateField;
    this.$scope.switchProgressCalculationCriteria = this.switchProgressCalculationCriteria;
    this.$scope.onTextareaClick = this.onTextareaClick;
    this.$scope.cloneMetricHere = this.cloneMetricHere;
    this.$scope.toggleReviewers = this.toggleReviewers;
    this.$scope.openCloneModal = this.openCloneModal;
    this.$scope.openMoveModal = this.openMoveModal;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.openMoveMetricModal = this.openMoveMetricModal;
    this.$scope.changeSession = this.changeSession;
    this.$scope.addChildObjective = this.addChildObjective;
    this.$scope.updateGoalParent = this.updateGoalParent;
    this.$scope.reorderGoalChild = this.reorderGoalChild;
    this.$scope.onReorderSuccess = this.onReorderSuccess;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.closeGoalDetailsModal = this.closeGoalDetailsModal;
    this.$scope.openDisableSyncModal = this.openDisableSyncModal;
    this.$scope.canUpdateMetric = this.canUpdateMetric;
    this.$scope.isGoalAbandoned = this.isGoalAbandoned;
    this.$scope.metricsContributeToGoalProgress = this.metricsContributeToGoalProgress;
    this.$scope.goalUnlockedAndSessionOpen = this.goalUnlockedAndSessionOpen;
    this.$scope.goalUnlockedUnabandonedSessionOpen = this.goalUnlockedUnabandonedSessionOpen;
    this.$scope.sessionWorkflowHasMetricFields = () => this.sessionWorkflowHasMetricFields();
    this.$scope.copyItemUrlToClipboard = this.copyItemUrlToClipboard;
    this.$scope.handleOpenCascadingForm = (metric, goalName) => this.cascadingMetricFormModal.handleOpenCascadingForm(metric, goalName);
    this.$scope.canCascadeMetric = (metric) => canCascadeMetric(metric, this.isGoalAbandoned());
    this.$scope.getInfoTooltipReason = this.getInfoTooltipReason;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);

    this.taskEvents.onTaskCreated((args) => this.onTaskCreated(args));
    this.taskEvents.onTaskDeleted((args) => this.onTaskDeleted(args));
    this.taskEvents.onTaskUpdated((args) => this.onTaskUpdated(args));
    this.$scope.$on("updateGoalDesignScore", (ev, designScoreForUpdate: IDesignScoreForUpdate) => {
      if (this.goalDataLoading) {
        this.designScoreForUpdate = designScoreForUpdate;
        return;
      }

      this.updateGoalDesignScore(designScoreForUpdate);
    });
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
    this.$scope.$on("$destroy", unsubscribe);

    return this.$q
      .all({
        isCloseOkrFeatureEnabled: this.featureToggleService.isFeatureAvailable("feature-close-okr"),
        isReorderGoalChildrenFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureReorderGoalChildren),
      })
      .then(({ isCloseOkrFeatureEnabled, isReorderGoalChildrenFeatureEnabled }) => {
        this.isCloseOkrFeatureEnabled = isCloseOkrFeatureEnabled;
        this.$scope.isReorderGoalChildrenFeatureEnabled = isReorderGoalChildrenFeatureEnabled;
        this.loadData();
      });
  };

  private addReturnStateForSharedUrl = (): void => {
    if (this.$stateParams.sharedGoal) {
      // the returnState is passed to _singleGoal() abstract state which uses the modal util method in routing
      const params = { ...this.$stateParams, sharedGoal: null, returnState: "^.^" };
      this.$state.go(".", params, { notify: false });
    }
  };

  private openCloneModal = (): void => {
    this.cloneOkrsModal.openForGoalClone(this.$scope.goal);
  };

  private openMoveModal = (): void => {
    this.goalModalsService.openModalForMove("goal", this.$scope.goal);
  };

  private updateField = (goal: IGoal, fields: string[]) => {
    this.$rootScope.traceAction(`update_OKR_fields-${fields.join("_")}`, () => {
      this.$scope.indicators.updatingField = true;

      const partialGoal = {};
      for (let i = 0; i < fields.length; i++) {
        if (typeof fields[i].split(".")[1] !== "undefined") {
          const top = fields[i].split(".")[0];

          partialGoal[top] = goal[top];
        } else {
          partialGoal[fields[i]] = goal[fields[i]];
        }
      }

      const includeMetrics = this.$state.current.name.includes(".tree.");
      // https://gtmhub.atlassian.net/browse/GVS-6795 - patchGoal method also broadcasts and loadData method is called twice
      this.goalService.patchGoal(goal.id, partialGoal, { includeMetrics: includeMetrics }).then(
        () => {
          this.okrEvents.broadcastGoalsChanged("goalChanged");
          this.$rootScope.$broadcast("goalsChanged");
          this.$scope.indicators.updatingField = false;
        },
        (error) => {
          this.$scope.indicators.updatingField = false;
          this.$scope.indicators.loadingGoalProgress = false;
          this.$scope.indicators.loadingSession = false;
          this.uiErrorHandlingService.handleModal(error);
        }
      );
    });
  };

  private confirmDeleteGoal = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-delete-goal.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.confirm = () => {
            ctrl.deleteGoal();
            $scope.$close();
          };
        },
      ],
    });
  };

  private deleteGoal = () => {
    this.$rootScope.traceAction("delete_OKR", () => {
      this.$scope.indicators.delete = true;
      this.goalService.deleteGoal(this.$scope.goal.id).then(
        () => {
          this.$rootScope.$broadcast("goalsChanged");
          this.$rootScope.$broadcast("goalDeleted", this.$scope.goal.id, this.$scope.goal);

          this.$scope.indicators.delete = false;
          this.$scope.$close();
        },
        () => {
          this.$scope.indicators.delete = false;
          this.$scope.error = "deleting";
        }
      );
    });
  };

  private getGoal = (): IPromise<void> => {
    return this.goalService.getGoalWithFields(this.$stateParams.id).then((goal) => {
      return this.getParentItem(goal).then((goal) => {
        this.$scope.goal = { ...goal, ...(goal.metrics && { metrics: this.enrichChildMetrics(goal.metrics) }) };
        this.$scope.isOwnedByEmployee = this.$scope.goal.assignee.type === "user";
        this.$scope.goalPoliciesMap = toKeyMap("policy", this.$scope.goal.designScore.policies);
        this.drawCircularProgressBar(this.$scope.goal.designScore.totalPoints);
        this.$scope.fake = {
          reviewers: this.generateFakeWorkflowReviewers(this.$scope.goal),
        };

        for (const metric of this.$scope.goal.metrics || []) {
          if (!metric.ownerIds || !metric.ownerIds.length) {
            metric.ownerIds = this.$scope.goal.ownerIds;
          }
        }

        // prettifying 'supported by' goals
        this.$scope.goal.supported = groupGoalsBySession(this.$scope.goal, "supported");
        this.$scope.goal.supporting = groupGoalsBySession(this.$scope.goal, "supporting");
      });
    });
  };

  private setCanChangeClosedStatus = (): void => {
    this.$scope.canChangeClosedStatus =
      this.$scope.goal.sessionId &&
      this.$scope.planningSession.status !== "closed" &&
      (hasCurrentUserRole("admin") ||
        this.$scope.goal.ownerIds.includes(getCurrentUserId()) ||
        isGoalOwnedByTeamOfEmployee(this.$scope.employee).some((teamId) => this.$scope.goal.ownerIds.includes(teamId)));
  };

  private getTasks = (): IPromise<void> => {
    return this.tasksService.getTasksV2({ filter: { parentId: this.$stateParams.id }, sort: "-dueDate" }).then((taskCollection) => {
      this.$scope.taskList = taskCollection.items;
    });
  };

  private getAllPolicies = (): IPromise<void> => {
    return this.policyService.getPolicies().then((allPolicies) => {
      this.$scope.allPoliciesMap = toKeyMap("fieldName", allPolicies);
    });
  };

  private getReduxItems = (): IPromise<void> => {
    return this.$scope.watchUntilAndRejectOnError(
      () => this.$scope.reduxItemsFetched,
      () => this.$scope.reduxItemsError
    );
  };

  private getParentOkr = () => {
    const goalSession = this.$scope.allSessions.find((it) => it.id === this.$scope.goal.sessionId);
    this.$scope.sessionsForParentSelector = [goalSession?.id, goalSession?.parentId];
    this.$scope.okrForParentSelector = {
      id: this.$scope.goal.parentId,
      type: this.$scope.goal.parentType,
    };
  };

  private getParentItem(goal: IGoal): IPromise<IGoal> {
    if (goal.parentId) {
      return goal.parentType === "goal"
        ? this.goalService.getGoal(goal.parentId).then((parentGoal) => {
            this.$scope.parentItem = parentGoal;
            return goal;
          })
        : this.metricService.getMetric(goal.parentId).then((parentMetric) => {
            this.$scope.parentItem = parentMetric;
            return goal;
          });
    }

    this.$scope.parentItem = null;
    return this.$q.resolve(goal);
  }

  private getSubGoalsIfNeeded = (): IPromise<void> => {
    if (this.$scope.goal.attainmentTypeString === "average_children" || this.$scope.goal.attainmentTypeString === "average_children_kr") {
      return this.goalService.getGoalsV2({ filter: { parentId: this.$scope.goal.id } }).then((subGoalsCollection) => {
        this.$scope.subGoals = this.enrichSubgoals(subGoalsCollection.items);
      });
    } else {
      return this.$q.resolve().then(() => {
        this.$scope.subGoals = [];
      });
    }
  };

  private getCurrentUserAllowedActions = (): IPromise<void> => {
    return this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.goal.id, "goal").then((currentUserAllowedActions) => {
      this.$scope.allowedActions = currentUserAllowedActions.currentUserActions;
    });
  };

  private getWorkflowActionsIfNeeded = (): IPromise<void> => {
    if (this.$scope.goal.workflow) {
      return this.goalService.getGoalWorkflowActions(this.$scope.goal.id).then((workflowActions) => {
        this.$scope.workflowUi = applyWorkflowConditions(generateWorkflowUi(workflowActions, this.$scope.goal, getCurrentUserId()));
      });
    } else {
      return this.$q.resolve();
    }
  };

  private checkMarketplaceSubmissionFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable("marketplace_partner_submission_of_okrs").then((enabled) => {
      // Submit to the Marketplace option is visible only to users in partner accounts, internal accounts and the Gtmhub live account
      const { type } = this.accountResolverService.getAccountData();
      this.$scope.isAllowedToSubmitToMarketplace =
        enabled && (isGtmhubAccountId(this.accountResolverService.getAccountId()) || type === AccountType.PartnerAccount || type === AccountType.InternalAccount);
    });
  };

  private setSessionToScope = (): void => {
    if (this.$scope.goal.sessionId) {
      const foundPlanningSession = this.$scope.allSessions.find((s) => s.id === this.$scope.goal.sessionId);
      this.$rootScope.planningSession = foundPlanningSession;
      this.$scope.planningSession = foundPlanningSession;
    }
  };

  private loadData = (): IPromise<void> => {
    this.goalDataLoading = true;

    this.checkMarketplaceSubmissionFeatureToggle();

    return this.$q
      .all([this.getGoal(), this.getTasks(), this.getAllPolicies(), this.getReduxItems()])
      .then(() => {
        this.goalDataLoading = false;

        this.$scope.shouldSeeOnboardingExperiment = this.onboardingService.shouldSeeOnboardingExperiment();
        this.setSessionToScope();
        this.setMethodologySettings();
        this.setCanChangeClosedStatus();
        this.adjustSessionDetails();
        this.getParentOkr();

        return this.$q.all([this.getSubGoalsIfNeeded(), this.getWorkflowActionsIfNeeded(), this.getCurrentUserAllowedActions()]).then(() => {
          this.$scope.allowEditGoalFields =
            this.$scope.planningSession.status === "open" &&
            !this.$scope.goal.locked &&
            this.$rootScope.hasPermission("ManageGoals") &&
            this.$scope.allowedActions.includes("update");
          if (this.$scope.isReorderGoalChildrenFeatureEnabled) {
            this.$scope.goalChildren = [...(this.$scope.goal.metrics || []), ...(this.$scope.subGoals || [])].sort((a, b) => a.orderId - b.orderId);
          }
          this.$scope.indicators.getGoal = false;
          this.$scope.indicators.loadingGoalProgress = false;
          this.$scope.indicators.loadingSession = false;

          if (this.designScoreForUpdate) {
            this.updateGoalDesignScore(this.designScoreForUpdate);
            this.designScoreForUpdate = null;
          }
        });
      })
      .catch((err) => {
        this.featureToggleService.isFeatureAvailable("okrs-shareable-urls").then((enabled) => {
          if (enabled && this.$scope.goal && this.$scope.goal.private && this.$scope.goal.obfuscated) {
            err.type = "private_okr";
            err.title = localize("this_okr_is_private");
          }
          this.handleIndicatorError(err, "getGoal", false);
        });
      });
  };

  private enrichSubgoals = (goals: IGoal[]): IGoal[] => {
    return goals.map((goal) => ({
      ...goal,
      color: this.progressColoring.getColorOrGeneric(goal.attainment),
      childType: "goal",
    }));
  };

  private enrichChildMetrics = (metrics: IMetric[]): IMetric[] => metrics.map((metric) => ({ ...metric, childType: "metric" }));

  private toggleReviewers = (): void => {
    this.$scope.workflowUi.showAllReviewers = !this.$scope.workflowUi.showAllReviewers;

    if (this.$scope.workflowUi.showAllReviewers) {
      this.$scope.workflowUi.limitReviewers = this.$scope.goal.workflow.reviewers.length;
    } else {
      this.$scope.workflowUi.limitReviewers = this.$scope.workflowUi.defaultLimitReviewers;
    }
  };

  private generateFakeWorkflowReviewers = (goal: IGoal): string[] => {
    const fakeUsersArr: string[] = [];

    if (!goal.workflow || !goal.workflow.reviewersNeeded || !goal.workflow.reviewers) {
      return fakeUsersArr;
    }

    const reviewersAdded: number = goal.workflow.reviewers.length;

    const fakeUsersCount: number = goal.workflow.reviewersNeeded - reviewersAdded >= 0 ? goal.workflow.reviewersNeeded - reviewersAdded : 0;

    for (let i = 0; i < fakeUsersCount; i++) {
      fakeUsersArr.push("");
    }

    return fakeUsersArr;
  };

  private selectMultipleOwners = (ids: string[]): void => {
    this.$scope.goal.assignees = ids.map((id) => this.$scope.assigneesIdMap[id]);
    this.$scope.goal.ownerIds = ids;

    this.updateField(this.$scope.goal, ["ownerIds", "assignees"]);
  };

  private closeGoalDetailsModal = (): void => {
    this.$scope.$dismiss();

    if (this.onboardingService.shouldSeeFirstOkrGuide()) {
      this.$rootScope.$broadcast("firstOkrCreated", this.$scope.planningSession);
    }
  };

  private adjustSessionDetails = () => {
    this.measureDueDateDifference();

    const totalDays = dayjs(this.$scope.planningSession.end).diff(this.$scope.planningSession.start) / 86400000;
    const sincePlanningSessionStart = dayjs().diff(this.$scope.planningSession.start) / 86400000;

    this.$scope.planningSession.timePassedPercentage = (sincePlanningSessionStart / totalDays) * 100;
  };

  private measureDueDateDifference = () => {
    const goal = this.$scope.goal;

    if (goal.metrics) {
      for (let i = 0; i < goal.metrics.length; i++) {
        if (goal.metrics[i].dueDate !== "0001-01-01T00:00:00Z") {
          const daysToComplete = dayjs(goal.metrics[i].dueDate).diff(this.$scope.planningSession.start) / 86400000;
          const totalDays = dayjs(this.$scope.planningSession.end).diff(this.$scope.planningSession.start) / 86400000;
          const percentOfTime = (daysToComplete / totalDays) * 100;
          const daysLeft = dayjs(goal.metrics[i].dueDate).diff(dayjs()) / 86400000;

          goal.metrics[i].percentTime = percentOfTime;
          goal.metrics[i].daysLeft = daysLeft;
        }

        if (goal.metrics[i].softDueDate !== "0001-01-01T00:00:00Z") {
          const daysToCompleteSoft = dayjs(goal.metrics[i].softDueDate).diff(this.$scope.planningSession.start) / 86400000;
          const totalDays = dayjs(this.$scope.planningSession.end).diff(this.$scope.planningSession.start) / 86400000;
          const percentOfTimeToSoft = (daysToCompleteSoft / totalDays) * 100;
          const daysLeft = dayjs(goal.metrics[i].softDueDate).diff(dayjs()) / 86400000;

          goal.metrics[i].percentTimeToSoftDeadline = percentOfTimeToSoft;
          goal.metrics[i].daysLeftToSoftDeadline = daysLeft;
        }
      }
    }
  };

  private confirmDeleteMetric = (index: number, metricId: string): void => {
    this.metricId = metricId;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.modal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete-metric.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IModalScope & { indicators: GoalIndicators; confirm(): void }) {
          $scope.indicators = ctrl.$scope.indicators;
          $scope.confirm = ctrl.deleteMetric;
        },
      ],
    });
  };

  private deleteMetric = () => {
    this.$rootScope.traceAction("delete_KR", () => {
      this.$scope.indicators.deleting = true;

      this.metricService.deleteMetric(this.$scope.goal.id, this.metricId).then(
        () => {
          this.$scope.indicators.deleting = false;

          for (let i = 0; i < this.$scope.goal.metrics.length; i++) {
            if (this.$scope.goal.metrics[i].id === this.metricId) {
              this.okrEvents.broadcastGoalsChanged("metricDeleted", {
                goalId: this.$scope.goal.id,
                metricId: this.$scope.goal.metrics[i].id,
              });

              this.$scope.goal.metrics.splice(i, 1);
              break;
            }
          }

          this.okrEvents.broadcastGoalsChanged("goalChanged");
          this.$rootScope.$broadcast("goalsChanged");
          if (this.modal) {
            this.modal.close();
          }
        },
        (err) => this.handleIndicatorError(err, "deleting", true)
      );
    });
  };

  private editReviewers = (): void => {
    if (this.$scope.workflowUi.owner) {
      this.$state.go("._approve.sendForApproval");
    }
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline: boolean): void => {
    this.$scope.error = "loadingError";
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private setAutofocus = (): void => {
    this.$scope.$broadcast("focusTextInput");
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (
      args.reason === "goalMoved" ||
      args.reason === "goalCreated" ||
      args.reason === "goalPatched" ||
      args.reason === "goalStatusChanged" ||
      args.reason === "goalChanged" ||
      args.reason === "metricLinked" ||
      args.reason === "metricUnlinked"
    ) {
      this.loadData();
    }
  };

  private onTaskCreated = (args: ITaskCreatedArgs): void => {
    if (args.task.parentId === this.$scope.goal.id) {
      this.$scope.taskList.unshift(args.task);
    }
  };

  private onTaskDeleted = (args: ITaskDeletedArgs) => {
    this.$scope.taskList = this.$scope.taskList.filter((task) => task.id !== args.task.id);
  };

  private onTaskUpdated = (args: ITaskUpdatedArgs) => {
    // if task changed parent goal => add to task list
    if (args.task.parentId === this.$scope.goal.id && !this.$scope.taskList.some((task) => task.id === args.task.id)) {
      this.$scope.taskList.unshift(args.task);
      return;
    }

    for (let i = 0; i < this.$scope.taskList.length; i++) {
      if (this.$scope.taskList[i].id === args.task.id) {
        // if parent goal of task is changed remove from goal's task list
        if (this.$scope.goal.id !== args.task.parentId) {
          this.$scope.taskList.splice(i, 1);
          return;
        }

        // and update task in list
        this.$scope.taskList[i] = args.task;
        return;
      }
    }
  };

  private updateGoalDesignScore = (designScoreForUpdate: IDesignScoreForUpdate) => {
    if (this.$scope.goal && designScoreForUpdate.goalId === this.$scope.goal.id) {
      this.$scope.goal.designScore = designScoreForUpdate.designScore;

      designScoreForUpdate.designScore.policies.forEach((policyForUpdate: IGoalPolicy) => {
        this.$scope.goalPoliciesMap[policyForUpdate.policy] = policyForUpdate;
      });

      this.drawCircularProgressBar(this.$scope.goal.designScore.totalPoints);
    }
  };

  private drawCircularProgressBar = (progressValue: number) => {
    this.$timeout(() => {
      const barEl = document.getElementById("left-side-circular-progress-bar");
      if (barEl) {
        barEl.style.transform = `rotate(${progressValue * 3.6}deg)`;
      }
    });
  };

  private switchProgressCalculationCriteria = (newCriteria: AttainmentTypeString): void => {
    this.$scope.indicators.loadingGoalProgress = true;
    this.$scope.goal.attainmentTypeString = newCriteria;

    const includeMetrics = this.$state.current.name.includes(".tree.");
    // https://gtmhub.atlassian.net/browse/GVS-6795 - patchGoal method also broadcasts and loadData method is called twice
    this.goalService.patchGoal(this.$scope.goal.id, { attainmentTypeString: newCriteria }, { includeMetrics }).then(
      () => {
        this.$rootScope.$broadcast("goalsChanged");
        this.okrEvents.broadcastGoalsChanged("goalChanged");
      },
      (error) => {
        this.$scope.indicators.loadingGoalProgress = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private onTextareaClick = ($event): void => {
    if ($event.target.tagName.toLowerCase() === "a") {
      $event.stopPropagation();
      $event.preventDefault();

      window.open($event.target.href, "_blank").focus();
    }
  };

  private cloneMetricHere = (metric: IMetric): void => {
    const newMetric = { ...metric, name: `${metric.name} clone` };
    delete newMetric.id;
    delete newMetric.trend;
    delete newMetric.confidence;
    // check if we have deactivated owners and remove them
    newMetric.ownerIds = this.assigneeService.getActiveAssigneesOnly(newMetric.ownerIds);
    this.metricService.createMetric(newMetric).then(
      (clonedMetric: IMetric) => {
        const limitV2 = 100;
        this.tasksService.cloneTasksRecursively({ currentPage: 1, limit: limitV2 }, { newParentIdsMap: { [metric.id]: clonedMetric.id }, parentIds: [metric.id] }).then(
          () => {
            this.$timeout(() => {
              this.afterClone(newMetric, clonedMetric);
            }, 500);
          },
          // https://gtmhub.atlassian.net/browse/GVS-6797 - "cloning metric tasks" is not a valid indicator -> EH is not working
          (error) => this.handleIndicatorError(error, "cloning metric tasks", true)
        );
      },
      // https://gtmhub.atlassian.net/browse/GVS-6797 - "loadingGoalProgress" seem not "turned on" -> EH may not be working
      (err) => this.handleIndicatorError(err, "loadingGoalProgress", true)
    );
  };

  private openCloneMetricModal = (metric: IMetric): void => {
    this.cloneOkrsModal.openForMetricClone(metric);
  };

  private openDisableSyncModal = (metricId: string): void => {
    this.disableMetricSyncModal.open(metricId);
  };

  private openMoveMetricModal = (metric: IMetric): void => {
    this.goalModalsService.openModalForMove("metric", metric, this.$scope.goal);
  };

  private afterClone = (originalMetric: IMetric, clonedMetric: IMetric) => {
    this.okrEvents.broadcastGoalsChanged("goalChanged");

    this.$rootScope.$broadcast("goalsChanged");
    this.okrEvents.broadcastGoalsChanged("metricCreated", { goalId: this.$scope.goal.id, metric: clonedMetric });

    const createCheckinPromise = (item) =>
      this.metricService.checkin(clonedMetric, item.checkin).catch((error) => this.handleIndicatorError(error, "cloningMetrics", true));

    handleRequestsInBatches(prepareCheckinData([originalMetric], getCurrentUserId()), createCheckinPromise, 1, this.$q);
  };

  private changeSession = (sessionId?: string): void => {
    if (!this.$scope.allowEditGoalFields) {
      return;
    }

    if (this.$scope.goal.sessionId === sessionId) {
      return;
    }

    this.$scope.indicators.loadingSession = true;
    this.$scope.goal.sessionId = sessionId;
    this.updateField(this.$scope.goal, ["sessionId", "parentId"]);
  };

  private addChildObjective = (itemId: string, itemType: ItemType, itemSessionId: string): void => {
    this.$state.go("._goalForm.form", {
      id: this.$scope.goal.id,
      parentId: itemId,
      parentType: itemType,
      externalSessionId: itemSessionId || this.$scope.goal.sessionId,
    });
  };

  private updateGoalParent = (parent: IGoalParent) => {
    const partialGoal: Partial<IGoal> = {
      parentId: parent?.id ?? "",
    };
    const includeMetrics = this.$state.current.name.includes(".tree.");

    this.goalService.patchGoal(this.$scope.goal.id, partialGoal, { includeMetrics }).then(
      () => {
        this.loadData();

        this.$rootScope.$broadcast("goalsChanged");
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private canUpdateMetric = (metric: IMetric): boolean => {
    if (!metric) {
      return false;
    }

    const isMetricOverdue = metric.daysLeft < 0;

    if (this.isCloseOkrFeatureEnabled) {
      return (
        !isMetricOverdue &&
        this.$rootScope.hasPermission("ManageGoals") &&
        !metric.sourceMetricId &&
        this.$scope.goal.closedStatus &&
        this.$scope.goal.closedStatus.status !== "abandoned"
      );
    } else {
      return !isMetricOverdue && this.$rootScope.hasPermission("ManageGoals") && !metric.sourceMetricId;
    }
  };

  private isGoalAbandoned = (): boolean => {
    return isOkrStatusAbandoned(this.$scope.goal.closedStatus, { isCloseOkrFeatureEnabled: this.isCloseOkrFeatureEnabled });
  };

  private metricsContributeToGoalProgress = (): boolean => {
    if (!this.$scope.goal) {
      return false;
    }

    return (
      !this.$scope.goal.attainmentTypeString || this.$scope.goal.attainmentTypeString === "average_kr" || this.$scope.goal.attainmentTypeString === "average_children_kr"
    );
  };

  private goalUnlockedAndSessionOpen = (): boolean => {
    if (!this.$scope.goal || !this.$scope.planningSession) {
      return false;
    }

    return this.$scope.planningSession.status === "open" && !this.$scope.goal.locked;
  };

  private sessionWorkflowHasMetricFields(): boolean {
    return this.$scope.planningSession.workflow?.editableKrFields?.length > 0;
  }

  private isFieldPartOfWorkflow(filedName: string): boolean {
    return this.$scope.planningSession.workflow?.editableOkrFields?.includes(filedName);
  }

  private goalUnlockedUnabandonedSessionOpen = (): boolean => {
    if (this.isCloseOkrFeatureEnabled) {
      return this.goalUnlockedAndSessionOpen() && this.$scope.goal.closedStatus && this.$scope.goal.closedStatus.status !== "abandoned";
    } else {
      return this.goalUnlockedAndSessionOpen();
    }
  };

  private getInfoTooltipReason = (): DisallowedMetricCreationReason => {
    if (this.$scope.goal.closedStatus?.status === "abandoned") {
      return "abandoned";
    }
    if (this.$scope.planningSession.status === PlanningSessionStatus.INPROGRESS) {
      return "session-in-progress";
    }
    if (this.$scope.goal.locked) {
      return "approved";
    }
  };

  private reorderGoalChild = (params: IDndDroppedParams<ContributorType>): IPromise<IOnDndDroppedCbResponse<IGoalChildReorderResponse>> => {
    const { items, item, oldIndex, newIndex } = params;
    const movedItem: IGoalChildReorderPayload = {
      parentId: this.$scope.goal.id,
      moved: {
        id: item.id,
        type: item.childType,
      },
    };

    const firstElementIndex = 0;
    const lastElementIndex = items.length - 1;

    switch (newIndex) {
      case firstElementIndex: {
        movedItem.next = {
          id: items[firstElementIndex].id,
          type: items[firstElementIndex].childType,
        };
        break;
      }
      case lastElementIndex: {
        movedItem.previous = {
          id: items[lastElementIndex].id,
          type: items[lastElementIndex].childType,
        };
        break;
      }
      default: {
        let indexPrevious: number;
        let indexNext: number;

        if (oldIndex < newIndex) {
          indexPrevious = newIndex;
          indexNext = newIndex + 1;
        } else {
          indexPrevious = newIndex - 1;
          indexNext = newIndex;
        }

        movedItem.previous = {
          id: items[indexPrevious].id,
          type: items[indexPrevious].childType,
        };
        movedItem.next = {
          id: items[indexNext].id,
          type: items[indexNext].childType,
        };
      }
    }

    this.$scope.indicators.updatingField = true;
    return this.goalService.reorderGoalChild(movedItem).then(
      (data) => {
        this.$scope.indicators.updatingField = false;
        return this.$q.resolve({ shouldProceed: true, data });
      },
      (error) => {
        this.$scope.indicators.updatingField = false;
        this.uiErrorHandlingService.handleModal(error);
        return this.$q.resolve({ shouldProceed: false });
      }
    );
  };

  private onReorderSuccess = (params: IDndInsertedParams<IGoalChildReorderResponse, ContributorType>): void => {
    const goalChildToBeUpdated = this.$scope.goalChildren.find((item) => item.id === params.item.id);
    if (goalChildToBeUpdated) {
      goalChildToBeUpdated.orderId = params.data.orderId;
    }

    if (params.item.childType === "metric") {
      const metricToBeUpdated = this.$scope.goal.metrics.find((item) => item.id === params.item.id);
      if (metricToBeUpdated) {
        metricToBeUpdated.orderId = params.data.orderId;
      }
    }

    if (params.item.childType === "goal") {
      const goalToBeUpdated = this.$scope.subGoals.find((item) => item.id === params.item.id);
      if (goalToBeUpdated) {
        goalToBeUpdated.orderId = params.data.orderId;
      }
    }
  };

  private copyItemUrlToClipboard = (itemId: string, type: "metric" | "goal"): void => {
    const copyToClipboardOptions = { container: getCopyURLNotificationEl(), duration: 3000 };
    const url = this.getShareableUrl(itemId, type);

    copyToClipboard(url, copyToClipboardOptions);
  };

  private getShareableUrl = (itemId: string, type: "metric" | "goal"): string => {
    const states = {
      alignmentTree: "gtmhub.goals.alignmentSidebar.tree",
      nested: "gtmhub.goals.alignmentSidebar.nested",
      metric: {
        grid: "gtmhub.goals.all._metric.metric",
        nested: "gtmhub.goals.alignmentSidebar.nested._goalSidebar.goal._metric.metric",
      },
      goal: {
        grid: "gtmhub.goals.all._goalSidebar.goal",
        nested: "gtmhub.goals.alignmentSidebar.nested._goalSidebar.goal",
      },
    };

    if (this.$state.current.name.includes(states.alignmentTree)) {
      return this.$state.href(states.alignmentTree, { ...this.$stateParams, sharedId: itemId, sharedParentId: itemId }, { absolute: true });
    }

    if (type === "goal") {
      if (this.$state.current.name.includes(states.nested)) {
        return this.$state.href(states.goal.nested, { ...this.$stateParams, id: itemId, sharedGoal: true }, { absolute: true });
      }
      return this.$state.href(
        states.goal.grid,
        { ...this.$stateParams, id: itemId, planningSessionId: this.$scope.goal.sessionId, sharedGoal: true },
        { absolute: true }
      );
    }

    if (type === "metric") {
      if (this.$state.current.name.includes(states.nested)) {
        return this.$state.href(states.metric.nested, { ...this.$stateParams, metricId: itemId, sharedMetric: true }, { absolute: true });
      }
      return this.$state.href(
        states.metric.grid,
        { ...this.$stateParams, metricId: itemId, planningSessionId: this.$scope.goal.sessionId, sharedMetric: true },
        { absolute: true }
      );
    }
  };

  private setMethodologySettings = (): void => {
    this.$scope.methodologySettings = {
      ...this.okrMethodologyFacade.getMethodologySettingsUIForGoalView(this.$scope.goal, this.$scope.planningSession),
      isProgressAggregationDisabled: this.okrMethodologyFacade.isProgressAggregationDisabled(this.$scope.planningSession?.settings),
    };
  };
}
